import type { AnalyticsEvent } from "@/types/models";

const X_EVENT_STORAGE_PREFIX = "awraq:x-event:";
// uwt.js sends each event to both hosts, falling back to these image requests when sendBeacon is unavailable.
const X_EVENT_URLS = ["https://analytics.twitter.com/1/i/adsct", "https://t.co/1/i/adsct"];
const X_ONETAG_EVENT = "4";
const sentEvents = new Set<string>();
let configuredEventCode: string | null = null;

/** The image pixel sends uwt.js's request without giving X's script access to family data, forms or cookies. */
export function initializeXPixel(eventCode?: string | null): void {
  configuredEventCode = eventCode && /^tw-[a-z0-9]+-[a-z0-9]+$/i.test(eventCode) ? eventCode : null;
}

export function trackXEventOnce(event?: AnalyticsEvent | null): void {
  if (!configuredEventCode || !event || event.name !== "sign_up" || sentEvents.has(event.id)) {
    return;
  }

  const storageKey = `${X_EVENT_STORAGE_PREFIX}${event.id}`;

  try {
    if (sessionStorage.getItem(storageKey)) {
      return;
    }
  } catch {
    // Storage may be disabled; the in-memory guard still deduplicates this visit.
  }

  // Only the successful registration response supplies this event. Never copy the current URL or form values.
  const parameters = new URLSearchParams({
    eci: X_ONETAG_EVENT,
    // conversion_id lets a later Conversions API event with the same id count once.
    event: JSON.stringify({ conversion_id: event.id }),
    event_id: crypto.randomUUID(),
    integration: "advertiser",
    p_id: "Twitter",
    p_user_id: "0",
    pl_id: crypto.randomUUID(),
    tw_document_href: new URL("/auth/register/info", window.location.origin).href,
    tw_iframe_status: "0",
    txn_id: configuredEventCode,
    type: "javascript",
    version: "2.4.12",
  });

  if (event.twclid) {
    parameters.set("twclid", event.twclid);
    // 1 is uwt.js's code for a click ID read from the landing URL.
    parameters.set("tw_clid_src", "1");
  }

  parameters.sort();

  for (const url of X_EVENT_URLS) {
    const pixel = new Image(1, 1);
    pixel.referrerPolicy = "no-referrer";
    pixel.src = `${url}?${parameters}`;
  }

  sentEvents.add(event.id);

  try {
    sessionStorage.setItem(storageKey, "1");
  } catch {
    // persistence-best-effort: the in-memory guard deduplicates; storage failure must not interrupt onboarding.
  }
}
